import LocationPicker from '../../../components/LocationPicker'
import DistrictPicker from '../../../components/DistrictPicker'
import type { BusinessType, CustomerForm } from './types'

interface CustomerFormModalProps {
  editingId: number | null
  form: CustomerForm
  businessTypes: BusinessType[]
  otroBusinessTypeId: number | null
  error: string
  saving: boolean
  showLocationPicker: boolean
  onShowLocationPicker: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  onLocationChange: (lat: number | null, lng: number | null) => void
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
}

/** Shared add/edit customer form, used by both StreetTab and WholesaleTab --
 * the fields are identical regardless of channel, only the default `channel`
 * value and the post-submit list each tab refreshes differ. */
export default function CustomerFormModal({
  editingId,
  form,
  businessTypes,
  otroBusinessTypeId,
  error,
  saving,
  showLocationPicker,
  onShowLocationPicker,
  onChange,
  onLocationChange,
  onSubmit,
  onCancel,
}: CustomerFormModalProps) {
  const handleDistrictChange = (value: string) =>
    onChange({ target: { name: 'district', value } } as unknown as React.ChangeEvent<HTMLSelectElement>)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-espresso-200">
          <h2 className="text-lg font-semibold text-espresso-800">
            {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
          </h2>
        </div>
        <form onSubmit={onSubmit} className="px-6 py-4 space-y-4">
          {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-espresso-700 mb-1">Nombre *</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={onChange}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Apodo</label>
              <input
                type="text"
                name="nickname"
                value={form.nickname}
                onChange={onChange}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Teléfono</label>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={onChange}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-espresso-700 mb-1">Dirección</label>
            <textarea
              name="address"
              value={form.address}
              onChange={onChange}
              rows={2}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
            />
          </div>
          <div>
            {showLocationPicker ? (
              <>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Ubicación en el mapa</label>
                <LocationPicker
                  latitude={form.latitude}
                  longitude={form.longitude}
                  onChange={(lat, lng) => onLocationChange(lat, lng)}
                />
              </>
            ) : (
              <button
                type="button"
                onClick={onShowLocationPicker}
                className="text-sm text-terracotta-700 hover:text-terracotta-800 underline"
              >
                + Agregar ubicación en el mapa (opcional)
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Tipo de Negocio</label>
              <select
                name="business_type_id"
                value={form.business_type_id || ''}
                onChange={onChange}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              >
                <option value="">Sin especificar</option>
                {businessTypes.map((bt) => (
                  <option key={bt.id} value={bt.id}>{bt.name}</option>
                ))}
              </select>
              {form.business_type_id === otroBusinessTypeId && (
                <input
                  type="text"
                  name="business_type_other"
                  value={form.business_type_other}
                  onChange={onChange}
                  placeholder="¿Qué tipo de negocio es?"
                  className="mt-2 w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              )}
            </div>
            <DistrictPicker value={form.district} onChange={handleDistrictChange} />
          </div>
          <div>
            <label className="block text-sm font-medium text-espresso-700 mb-1">Canal de Venta</label>
            <select
              name="channel"
              value={form.channel}
              onChange={onChange}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
            >
              <option value="street">Calle</option>
              <option value="wholesale">Mayorista (bodega / tienda)</option>
            </select>
            {form.channel === 'wholesale' && (
              <p className="mt-1 text-xs text-espresso-500">
                Las cuentas mayoristas necesitan un precio pactado por producto antes de poder venderles.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Modo de Pago</label>
              <select
                name="payment_mode"
                value={form.payment_mode}
                onChange={onChange}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              >
                <option value="cash">Efectivo</option>
                <option value="credit">Crédito</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">DNI</label>
              <input
                type="text"
                name="dni"
                value={form.dni}
                onChange={onChange}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">RUC</label>
              <input
                type="text"
                name="ruc"
                value={form.ruc}
                onChange={onChange}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Ciclo de Reorden (días)</label>
              <input
                type="number"
                name="cycle_days"
                value={form.cycle_days}
                onChange={onChange}
                min={1}
                max={365}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              />
            </div>
          </div>
          <p className="text-xs text-espresso-500">Frecuencia de reorden de este cliente (predeterminado: 30 días)</p>
          <div>
            <label className="block text-sm font-medium text-espresso-700 mb-1">Detalles</label>
            <textarea
              name="details"
              value={form.details}
              onChange={onChange}
              rows={2}
              placeholder="Observaciones adicionales sobre este cliente"
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
            >
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
